import React, { useEffect, useState, useRef, useMemo } from 'react';
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  addDoc, 
  deleteDoc, 
  doc, 
  getDocs,
  orderBy, 
  serverTimestamp, 
  updateDoc 
} from 'firebase/firestore';
import { User as UserIcon, Send, MessageSquare, AlertCircle, Loader2, CheckCircle2, HelpCircle, X } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { Button } from './Button';
import { db } from '../firebase';
import { ChatSession, Message } from '../types';

export const Consult: React.FC = () => {
  const { user, setIsLoginOpen } = useAuth();
  const [activeSession, setActiveSession] = useState<ChatSession | null>(null);
  const [waitingSessions, setWaitingSessions] = useState<ChatSession[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const availability = useMemo(() => {
    const now = new Date();
    const day = now.getDay();
    const hour = now.getHours();
    const isAvailable = day >= 1 && day <= 6 && hour >= 10 && hour < 18;
    return { isAvailable, day, hour };
  }, []);

  useEffect(() => {
    if (!user) {
      setIsInitialLoading(false);
      return;
    }

    let unsubscribers: (() => void)[] = [];

    if (user.isConsultant) {
      const qWaiting = query(collection(db, "chatSessions"), where("status", "==", "waiting"));
      const unsubWaiting = onSnapshot(qWaiting, (snapshot) => {
        const sessions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ChatSession));
        setWaitingSessions(sessions);
        setIsInitialLoading(false);
      });
      unsubscribers.push(unsubWaiting);

      const qActive = query(
        collection(db, "chatSessions"), 
        where("consultantEmail", "==", user.email || user.mobile),
        where("status", "==", "active")
      );
      const unsubActive = onSnapshot(qActive, (snapshot) => {
        if (!snapshot.empty) {
          setActiveSession({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as ChatSession);
        } else {
          setActiveSession(null);
        }
        setIsInitialLoading(false);
      });
      unsubscribers.push(unsubActive);
    } else {
      const q = query(collection(db, "chatSessions"), where("customerEmail", "==", user.email || user.mobile));
      const unsubCustomer = onSnapshot(q, (snapshot) => {
        if (!snapshot.empty) {
          setActiveSession({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as ChatSession);
        } else {
          setActiveSession(null);
        }
        setIsInitialLoading(false);
      });
      unsubscribers.push(unsubCustomer);
    }

    return () => unsubscribers.forEach(unsub => unsub());
  }, [user]);

  useEffect(() => {
    if (!activeSession) {
      setMessages([]);
      return;
    }

    const q = query(
      collection(db, "chatSessions", activeSession.id, "messages"), 
      orderBy("timestamp", "asc")
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Message));
      setMessages(msgs);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    });

    return () => unsubscribe();
  }, [activeSession?.id]);

  const startChat = async () => {
    if (!user) return;
    const displayName = user.fullName || user.firstName || user.mobile;
    try {
      await addDoc(collection(db, "chatSessions"), {
        customerEmail: user.email || user.mobile,
        customerName: displayName,
        consultantEmail: null,
        consultantName: null,
        status: 'waiting',
        userQuotaRemaining: 5,
        waitingForReply: false,
        lastMessageAt: serverTimestamp(),
        lastSenderRole: 'customer',
        createdAt: serverTimestamp()
      });
    } catch (error: any) {
      console.error("Chat start error:", error);
    }
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeSession || !user) return;
    if (newMessage.length > 300) return;

    if (!user.isConsultant) {
      const remaining = Number(activeSession.userQuotaRemaining ?? 5);
      if (remaining <= 0) return;
    }

    const displayName = user.fullName || user.firstName || user.mobile;
    const currentUserId = user.uid || user.email || user.mobile;

    try {
      // FIX: Added robust identity markers so left/right logic never breaks again
      await addDoc(collection(db, "chatSessions", activeSession.id, "messages"), {
        text: newMessage.trim(),
        senderId: currentUserId,
        senderEmail: user.email || null,
        senderMobile: user.mobile || null,
        senderRole: user.isConsultant ? 'consultant' : 'customer',
        senderName: displayName,
        timestamp: serverTimestamp()
      });

      if (user.isConsultant) {
        await updateDoc(doc(db, "chatSessions", activeSession.id), {
          userQuotaRemaining: 5,
          waitingForReply: false,
          lastMessageAt: serverTimestamp(),
          lastSenderRole: 'consultant',
        });
      } else {
        const remaining = Number(activeSession.userQuotaRemaining ?? 5);
        const nextRemaining = Math.max(0, remaining - 1);
        await updateDoc(doc(db, "chatSessions", activeSession.id), {
          userQuotaRemaining: nextRemaining,
          waitingForReply: true,
          lastMessageAt: serverTimestamp(),
          lastSenderRole: 'customer',
        });
      }
      setNewMessage('');
    } catch (error) {
      console.error("Message send error:", error);
    }
  };

  const clearChat = async () => {
    if (!activeSession || !user?.isConsultant) return;
    try {
      const snap = await getDocs(collection(db, "chatSessions", activeSession.id, "messages"));
      await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
      await updateDoc(doc(db, "chatSessions", activeSession.id), {
        userQuotaRemaining: 5,
        waitingForReply: false,
        lastMessageAt: serverTimestamp(),
      });
    } catch (error) { console.error(error); }
  };

  const endChat = async () => {
    if (!activeSession || !user?.isConsultant) return;
    try {
      await deleteDoc(doc(db, "chatSessions", activeSession.id));
      setActiveSession(null);
    } catch (error) { console.error(error); }
  };

  if (isInitialLoading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-20">
        <Loader2 className="animate-spin text-orange-500 mb-4" size={32} />
        <p className="text-stone-500 font-bold uppercase text-sm tracking-widest">Syncing...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-md text-center">
        <h1 className="text-2xl font-serif text-stone-900 mb-6 font-bold uppercase tracking-widest">Expert Consultation</h1>
        <div className="bg-white p-8 rounded-3xl shadow-sm border border-orange-100">
          <p className="mb-6 text-sm font-bold uppercase text-stone-700">Sign in to speak with a curator.</p>
          <button onClick={() => setIsLoginOpen(true)} className="w-full bg-orange-600 text-white py-4 rounded-xl font-black uppercase text-sm">
            Sign In
          </button>
        </div>
      </div>
    );
  }

  const remainingQuota = Number(activeSession?.userQuotaRemaining ?? 5);
  const isUserBlocked = !user.isConsultant && remainingQuota <= 0;
  const currentUserId = user.uid || user.email || user.mobile;

  return (
    <div className="flex-1 p-4 md:p-6 flex justify-center items-start md:items-center min-h-[calc(100vh-160px)]">
      {activeSession ? (
        <div 
          className="flex flex-col bg-white rounded-3xl shadow-2xl border border-stone-200 overflow-hidden w-full max-w-4xl relative animate-[fadeIn_0.3s_ease-out]"
          style={{ height: 'calc(100vh - 180px)', maxHeight: '750px', minHeight: '500px' }} 
        >
          
          {/* Header */}
          <div className="shrink-0 p-4 md:p-5 bg-stone-50 border-b border-stone-200 flex flex-col sm:flex-row justify-between items-center gap-4 relative z-10">
            <div className="flex items-center gap-3">
               <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
               <div>
                  <p className="text-xs uppercase font-bold text-stone-500 tracking-wider">Secure Channel</p>
                  <h3 className="font-bold text-lg text-stone-900">
                    {user.isConsultant ? activeSession.customerName : (activeSession.consultantName || 'Expert Support')}
                  </h3>
               </div>
            </div>
            
            {!user.isConsultant && (
              <div className="relative flex items-center gap-2 bg-orange-50 px-4 py-1.5 rounded-full border border-orange-100">
                <AlertCircle size={14} className="text-orange-600" />
                <span className="text-xs font-bold uppercase text-orange-700">
                  {isUserBlocked ? 'Limit Reached' : `${remainingQuota}/5 Left`}
                </span>
                
                {/* Information Trigger */}
                <button 
                  type="button"
                  onClick={() => setShowTooltip(!showTooltip)}
                  className="text-orange-400 hover:text-orange-600 transition-colors focus:outline-none ml-1"
                >
                  <HelpCircle size={14} />
                </button>

                {/* Popover Tooltip */}
                {showTooltip && (
                  <div className="absolute top-10 right-0 w-64 p-3 bg-stone-900 text-white text-sm leading-relaxed rounded-xl shadow-xl z-50">
                    <button 
                      onClick={() => setShowTooltip(false)}
                      className="absolute top-2 right-2 text-stone-400 hover:text-white"
                    >
                      <X size={12} />
                    </button>
                    <p className="pr-4">You can send up to 5 messages at a time to prevent spam. This limit resets automatically as soon as our expert replies!</p>
                  </div>
                )}
              </div>
            )}

            {user.isConsultant && (
              <div className="flex items-center gap-2">
                <Button onClick={clearChat} variant="outline" className="text-xs h-8 rounded-lg border-stone-200 text-stone-600">Clear</Button>
                <Button onClick={endChat} variant="outline" className="text-xs h-8 rounded-lg border-red-100 text-red-600 hover:bg-red-50">End Chat</Button>
              </div>
            )}
          </div>
          
          {/* Scrollable Messages Area */}
          <div className="flex-1 overflow-y-auto min-h-0 p-4 md:p-6 space-y-6 bg-stone-50/30 custom-scrollbar">
            {messages.map((msg) => {
              // FIX: Bulletproof check to see if the message belongs to the current user
              const isMe = msg.senderRole 
                ? (user.isConsultant ? msg.senderRole === 'consultant' : msg.senderRole === 'customer')
                : (msg.senderId === currentUserId || 
                  (msg.senderEmail && user.email && msg.senderEmail === user.email) ||
                  (msg.senderMobile && user.mobile && msg.senderMobile === user.mobile));

              return (
                <div key={msg.id} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                  <p className="text-xs font-bold text-stone-400 mb-1 mx-2 uppercase tracking-tighter">
                    {isMe ? 'You' : (msg.senderName || 'Expert')}
                  </p>
                  <div className={`max-w-[85%] md:max-w-[70%] p-4 rounded-2xl text-base font-medium shadow-sm leading-relaxed ${
                    isMe 
                      ? 'bg-orange-600 text-white rounded-br-none' 
                      : 'bg-white text-stone-800 rounded-bl-none border border-stone-100'
                  }`}>
                    {msg.text}
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <form onSubmit={sendMessage} className="shrink-0 p-4 bg-white border-t border-stone-200">
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center px-1">
                <span className="text-xs font-bold text-stone-400 uppercase tracking-widest">
                  {isUserBlocked ? 'Wait for reply to unlock messages' : 'Send Message'}
                </span>
                <span className={`text-xs font-bold ${newMessage.length >= 300 ? 'text-red-500' : 'text-stone-400'}`}>
                  {newMessage.length}/300
                </span>
              </div>
              
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  maxLength={300}
                  disabled={isUserBlocked}
                  placeholder={isUserBlocked ? "Quota exhausted..." : "Type your inquiry..."}
                  className="flex-1 px-5 py-3.5 text-base rounded-2xl border border-stone-200 bg-stone-50 text-stone-900 focus:outline-none focus:border-orange-500 font-medium disabled:opacity-50 transition-all"
                />
                <button 
                  type="submit" 
                  disabled={!newMessage.trim() || isUserBlocked}
                  className="px-5 bg-orange-600 text-white rounded-2xl hover:bg-orange-700 disabled:opacity-50 transition-all shadow-lg shadow-orange-100 flex items-center justify-center"
                >
                  <Send size={18} />
                </button>
              </div>
            </div>
          </form>
        </div>
      ) : (
        /* Empty State */
        <div className="flex-1 flex flex-col items-center justify-center text-center max-w-2xl mx-auto py-12">
          <div className="mb-6 p-6 bg-orange-50 rounded-full border border-orange-100">
            <MessageSquare size={48} className="text-orange-600" />
          </div>
          <h1 className="text-3xl font-serif text-stone-900 mb-4 tracking-tight">Expert Consultation</h1>
          <p className="text-base text-stone-500 mb-8 max-w-sm font-medium">Connect with our curators for gemstone verification and order assistance.</p>
          <div className="bg-white p-8 rounded-3xl shadow-xl border border-stone-100 w-full max-w-sm">
              <Button onClick={startChat} className="w-full py-4 rounded-xl font-bold uppercase text-sm tracking-widest">
                Start Chat Session
              </Button>
          </div>
        </div>
      )}
    </div>
  );
};
